import { Component, Input, Output, ElementRef, EventEmitter, ViewChild, OnInit } from '@angular/core';
import { CfCoreComponent } from '../core/core.component';
import { TreeviewModel } from '../../models/treeview/treeview.model';
import { TreeviewStylingModel } from '../../models/treeview/treeview-styling.model';
import { JsonEditorModel } from '../../models/json-editor/json-editor.model';
import { JsonEditorStylingModel } from '../../models/json-editor/json-editor-styling.model';
import { JsonEditorRowButtonModel } from '../../models/json-editor/json-editor-row-button.model';
import { JsonEditorTemplates } from '../../templates/json-editor.template';
import { TemplateService } from '../../services/template-service/template.service';
import { CfTreeviewComponent } from '../../components/treeview/treeview.component';
/**
 * <p>cf-json-editor component is based on cf-treeview</p>
 * <p>Example of using:</p>
 * <pre>
 * <code><</code>cf-json-editor [properties]="myJsonEditor"
 * [styling]="myJsonEditorStyles"<code>></code><code><</code><code>/</code>cf-json-editor<code>></code>
 * </pre>
 */
export class CfJsonEditorComponent extends CfCoreComponent implements OnInit {
/**
 * @hidden 
 * Link to the cf-treeview-component
 */
public treeviewElement: CfTreeviewComponent;
/**
 * <p>JsonEditorModel object.</p>
 * <p>Example of model:</p>
 * <pre>
 * {{'{'}}
 *  data: this.myJsonObject,
 *  lockKeys: true,
 *  showButtons: "hover",
 *  rowButtons: [
 *    [
 *      {{'{'}} "name": "moveUp", "icon": "keyboard_arrow_up" {{'}'}},
 *      {{'{'}} "name": "moveDown", "icon": "keyboard_arrow_down" {{'}'}}
 *    ],
 *    [
 *      {{'{'}} "name": "indent", "icon": "format_indent_increase" {{'}'}},
 *      {{'{'}} "name": "outdent", "icon": "format_indent_decrease" {{'}'}}
 *    ],
 *    [
 *      {{'{'}} "name": "clone", "icon": "content_copy" {{'}'}},
 *      {{'{'}} "name": "delete", "icon": "delete" {{'}'}},
 *      {{'{'}} "name": "insert", "icon": "playlist_add" {{'}'}}
 *    ]
 *  ]
 * {{'}'}}
 * </pre>
 * <h4>data: object</h4>
 * <p>JSON input to be converted into tree</p>
 * <h4>lockKeys: boolean</h4>
 * <p>Whether to prevent keys from being edited. Default: true.</p>
 * <h4>rowButtons: [ JsonEditorRowButtonModel[] ]</h4>
 * <p>Array of buttons to be rendered per level</p>
 * <h4>showButtons: string</h4>
 * <p>Circumstances to show buttons in. Can be: hover, select or always.</p>
 */
properties: JsonEditorModel;
/**
 * Object for all styling properties:
 * <h4>container: StylingModel</h4>
 * <p>Styling properties for main json-editor container</p>
 * <h4>header: StylingModel</h4>
 * <p>Styling properties for header section</p>
 * <h4>treeview: TreeviewStylingModel</h4>
 * <p>Styling properties for cf-treeview</p>
 */
styling: JsonEditorStylingModel;
/**
 * <p>Whether to prevent keys from being edited. Default: true.</p>
 * @param {?} value
 * @return {?}
 */
set lockKeys(value: boolean) { this.properties.lockKeys = value; }
/**
 * <p>Circumstances to show buttons in. Can be: hover, select or always.</p>
 * @param {?} value
 * @return {?}
 */
set showButtons(value: string) { this.properties.showButtons = value; }
/**
 * <p>JSON input to be converted into tree</p>
 * @param {?} value
 * @return {?}
 */
set data(value: any) { this.properties.data = value; }
/**
 * <p>Array of buttons to be rendered per level</p>
 * @param {?} value
 * @return {?}
 */
set rowButtons(value: [ JsonEditorRowButtonModel[] ]) { this.properties.rowButtons = value; }
/**
 * Emit event with action (name with icon) and optional item from which action was triggered
 */
onCustomAction: EventEmitter<any> = new EventEmitter<any>();
/**
 * Emit event with converted json object
 */
onDataConvert: EventEmitter<any> = new EventEmitter<any>();
/**
 * @hidden 
 * <p>String being filtered on</p>
 */
filterBy: string;
/**
 * @hidden 
 * <p>toggled to true to flash a red warning sometimes</p>
 */
illegalOperation = false;
/**
 * @hidden 
 * <p>the array that the tree will be built around, since the Treeview takes an array, not JSON</p>
 */
tree: Array<{}>;
/**
 * @hidden 
 * <p>Model for the underlying treeview component</p>
 */
treeViewModel: TreeviewModel;
/**
 * @hidden 
 * <p>property for output JSON to be written to</p>
 */
JsonOutput;
/**
 * @hidden
 * @param {?} el
 * @param {?} templateService
 */
constructor(el: ElementRef, templateService: TemplateService) {
    super(el, templateService);
    this.properties = new JsonEditorModel();
    this.styling = new JsonEditorStylingModel()
  }
/**
 * @hidden 
 * <p>optional custom filter function--return true to show that node when filtering</p>
 * @param {?} node
 * @return {?}
 */
filterFunction(node) {
    return node.data.key.startsWith(this.filterBy);
  };
/**
 * @hidden
 * @return {?}
 */
ngOnInit() {
    this.getMyTemplate('json-editor', JsonEditorTemplates).then(() => {
      this.mergeProperties(this.activeTemplate.property, this.properties, 'JSON-ED').then( sampler => {
        if (this.properties.data) {
          this.tree = this.createChildren(this.properties.data);
          this.treeViewModel = new TreeviewModel({
            items: this.tree,
            options: {
              idField: '_id',
              displayField: '_name',
              childrenField: '_children',
              isExpandedField: '_expanded',
              selectable: false,
              allowDrag: true,
              animateExpand: false
            }
          });
        }
        // console.log('JSON-ED', this);
      });
    });
  }
/**
 * @hidden 
 * Creates the base array for the Treeview, called later for nodes with children to create arrays of children
 * @param {?} json  input JSON to convert to array of nodes
 * @param {?=} lockEdit  whether to prevent editing of these nodes
 * @return {?}
 */
createChildren(json: {}, lockEdit = false): Array<{}> {
    let /** @type {?} */ tree = [];
    let /** @type {?} */ keys = Object.keys(json);
    let /** @type {?} */ value;
    keys.forEach((key, idx) => {
      tree.push(this.createTreeNode(key, json[key], lockEdit));
    });
    return tree;
  }
/**
 * @hidden 
 * Creates a tree node from a JSON key-value pair
 * @param {?} key string to be used for the key
 * @param {?} value the value of the key-value pair
 * @param {?} lockEdit whether to prevent editing of this node
 * @return {?}
 */
createTreeNode(key: string, value, lockEdit) {
    // let type = Array.isArray(value) ? typeof value[0] : typeof value;
    let /** @type {?} */ type = typeof value;
    let /** @type {?} */ id = Math.random();
    let /** @type {?} */ node = {
      key: key,
      value: value,
      type: type,
      _id: '' + id,
      _expanded: false,
      _name: 'child' + id,
      _children: null,
      isArray: false,
      lockEdit: lockEdit
    };
    if (typeof value === 'object' && value !== null) {
      node.isArray = Array.isArray(value);
      node._children = this.createChildren(value, lockEdit);
    }
    return node;
  }
/**
 * Updates the Treeview
 * @return {?}
 */
updateTree() {
    this.treeviewElement.tree.treeModel.update();
  }
/**
 * @hidden 
 * Taking the whole tree and a node, returns the node, its parent node, its parent array, and its level in the tree
 * @param {?} tree the tree or section or tree to locate the node in
 * @param {?} node the node you are locating
 * @param {?=} level the level at which this was called, default top level (0)
 * @return {?}
 */
findNode(tree, node, level = 0) {
    let /** @type {?} */ results, /** @type {?} */ parent, /** @type {?} */ parentNode;
    level += 1;
    if (Array.isArray(tree)) {
      parentNode = null;
      parent = tree;
    } else {
      parentNode = tree;
      parent = tree._children;
    }
    parent.forEach(child => {
      if (child._id === node._id) {
        results = { child, parent, level, parentNode };
      } else if (child._children && !results) {
        results = this.findNode(child, node, level);
      }
    });
    return results;
  }
/**
 * @hidden 
 * Removes node from parent
 * @param {?} parent the node containing the node to be removed
 * @param {?} node the node you are removing
 * @return {?}
 */
removeNode(parent, node) {
    let /** @type {?} */ tree = Array.isArray(parent) ? parent : parent._children;
    let /** @type {?} */ idx = tree.indexOf(node);
    tree.splice(idx, 1);
    this.updateTree();
  }
/**
 * @hidden 
 * Locates a node within a given tree and removes it
 * @param {?} tree the tree containing the node to be removed
 * @param {?} node the node you are removing
 * @return {?}
 */
findAndRemoveNode(tree, node) {
    let /** @type {?} */ location = this.findNode(tree, node);
    this.removeNode(location.parent, location.child);
  }
/**
 * @hidden 
 * Inserts a node as a child of another node at the given position
 * @param {?} parent the node to be given the child
 * @param {?} node the node make the child
 * @param {?=} idx
 * @return {?}
 */
insertNode(parent, node, idx = 0) {
    let /** @type {?} */ tree = Array.isArray(parent) ? parent : parent._children;
    tree.splice(idx, 0, node);
    this.updateTree();
  }
/**
 * @hidden 
 * Inserts a blank node as the last child of another node
 * @param {?} parent the node to be given the child
 * @return {?}
 */
addBlankNode(parent) {
    let /** @type {?} */ key = (parent.isArray && parent._children) ? String(parent._children.length) : 'key';
    let /** @type {?} */ id = Math.random();
    let /** @type {?} */ tempNode = {
      key: key,
      value: 'value',
      type: 'string',
      lockEdit: false,
      _id: '' + id,
      _expanded: false,
      _name: 'child' + id,
      _children: null,
      isArray: false
    };
    let /** @type {?} */ tempIndex = parent._children ? parent._children.length : 0;
    if (parent.type !== 'string') {
      this.insertNode(parent, tempNode, tempIndex);
      if (parent._id) {
        let /** @type {?} */ containingNode = this.treeviewElement.tree.treeModel.getNodeById(parent._id);
        containingNode.expand();
      }
    }
    return;
  }
/**
 * @hidden 
 * Takes a node and creates an identical node and adds it as a sibling
 * @param {?} tree a tree containing of the node to be cloned
 * @param {?} node the node to be cloned
 * @return {?}
 */
duplicateNode(tree, node) {
    let /** @type {?} */ location = this.findNode(tree, node);
    let /** @type {?} */ parent = location.parent;
    let /** @type {?} */ idx = parent.indexOf(node);
    this.insertNode(parent, this.cloneNode(node, true), idx + 1);
  }
/**
 * @hidden 
 * Replaces a node with a different node
 * @param {?} tree a tree containing the node to be replaced
 * @param {?} oldNode the node to be replaced
 * @param {?} newNode the node to replace it with
 * @return {?}
 */
replaceNode(tree, oldNode, newNode) {
    let /** @type {?} */ location = this.findNode(tree, oldNode);
    let /** @type {?} */ parent = location.parent;
    let /** @type {?} */ idx = parent.indexOf(oldNode);
    parent.splice(idx, 1, newNode);
    this.updateTree();
  }
/**
 * @hidden 
 * Given a node, makes a copy
 * @param {?} node the node to be cloned
 * @param {?=} highest whether the node is on the highest level, so that there are no duplicate keys
 * @param {?=} arrayLength
 * @return {?}
 */
cloneNode(node, highest = false, arrayLength = 0) {
    let /** @type {?} */ cloneChildren = [];
    if (node._children) {
      node._children.forEach(child => {
        cloneChildren.push(this.cloneNode(child, false, node.value.length));
      });
    }
    let /** @type {?} */ modifiedKey = arrayLength ? arrayLength : node.key + '_copy';
    let /** @type {?} */ id = Math.random();
    let /** @type {?} */ newNode = {
      key: highest ? modifiedKey : node.key,
      value: node.value,
      type: node.type,
      lockEdit: false,
      _id: id,
      _name: 'child' + id,
      _expanded: false,
      _children: cloneChildren.length ? cloneChildren : null,
      isArray: node.isArray
    };
    return newNode;
  }
/**
 * @hidden 
 * Given a node, changes the value of a field
 * @param {?} value the new value for the field
 * @param {?} node the node to be edited
 * @param {?} field the field to be edited
 * @return {?}
 */
editNode(value, node, field) {
    node[field] = value;
    if (field === 'value' && value === 'array' || value === 'object') {
      node.type = value === 'array' ? 'string' : 'object';
      if (value === 'array') {
        node.isArray = true;
        node.type = 'string';
        node.value = [];
      } else {
        node.type = 'object';
        node.value = {};
      }
      node._children = [];
    } else {
      node.type = 'string';
      node._children = null;
    }
    this.updateTree();
  }
/**
 * @hidden 
 * Bumps a node up or down among its siblings
 * @param {?} tree
 * @param {?} node the node to be moved
 * @param {?} direction 'up' or 'down'
 * @return {?}
 */
moveNode(tree, node, direction) {
    let /** @type {?} */ location = this.findNode(tree, node);
    let /** @type {?} */ parent = location.parent;
    let /** @type {?} */ idx = parent.indexOf(node);
    let /** @type {?} */ modifier = direction === 'up' ? -1 : 1;
    let /** @type {?} */ prevNode = parent[idx + modifier];
    if (prevNode) {
      parent[idx + modifier] = node;
      parent[idx] = prevNode;
      this.updateTree();
    }
  }
/**
 * @hidden 
 * Makes a node the child of the node before it or creates a parent to make it a child of
 * @param {?} tree the containing tree of the node to be indented
 * @param {?} node the node to be indented
 * @return {?}
 */
indentNode(tree, node) {
    let /** @type {?} */ location = this.findNode(tree, node);
    let /** @type {?} */ parent = location.parent;
    let /** @type {?} */ child = location.child;
    let /** @type {?} */ idx = parent.indexOf(child);
    let /** @type {?} */ newParent = parent[idx - 1];
    let /** @type {?} */ id;
    if (!newParent || newParent.lockEdit) {
      id = Math.random();
      newParent = {
        key: 'key',
        value: {},
        type: 'object',
        lockEdit: false,
        _id: id,
        _expanded: false,
        _name: 'child' + id,
        _children: [child],
        isArray: false,
      };
      newParent.value[child.key] = child.value;
      parent[idx] = newParent;
      this.updateTree();
    } else if (newParent._children) {
      newParent._children.push(child);
      newParent._expanded = true;
      this.updateTree();
      this.removeNode(parent, child);
    } else {
      newParent.type = 'object';
      newParent.value = {};
      newParent._children = [];
      newParent._children.push(child);
      newParent.value[child.key] = child.value;
      newParent._expanded = true;
      this.updateTree();
      this.removeNode(parent, child);
    }
    id = id ? id : newParent._id;
    let /** @type {?} */ containingNode = this.treeviewElement.tree.treeModel.getNodeById(id);
    containingNode.expand();
    return;
  }
/**
 * @hidden 
 * Makes a child the sibling of its parent
 * @param {?} tree the containing tree of the node to be outdented
 * @param {?} node the node to be outdented
 * @return {?}
 */
outdentNode(tree, node) {
    let /** @type {?} */ location = this.findNode(tree, node);
    if (location.parentNode === null) {
      this.flashIllegal();
      return;
    }
    let /** @type {?} */ parentLocation = this.findNode(tree, location.parentNode);
    let /** @type {?} */ uncles = parentLocation.parent;
    let /** @type {?} */ idx = uncles.indexOf(parentLocation.child);
    this.removeNode(location.parent, node);
    this.updateTree();
    this.insertNode(uncles, node, idx + 1);
  }
/**
 * @hidden 
 * Is called when a node is moved
 * @param {?} e the event
 * @return {?}
 */
onMoveNode(e) {
    // console.log(e);
  }
/**
 * expands all nodes
 * @return {?}
 */
expandAll() {
    this.treeviewElement.tree.treeModel.expandAll();
  }
/**
 * collapses all nodes
 * @return {?}
 */
collapseAll() {
    this.treeviewElement.tree.treeModel.collapseAll();
  }
/**
 * @hidden 
 * checks to see if a node is at the highest level in a given tree
 * @param {?} tree the containing tree
 * @param {?} node the node being checked
 * @return {?}
 */
isTopLevel(tree, node) {
    let /** @type {?} */ topLevel;
    let /** @type {?} */ location = this.findNode(tree, node);
    if (location) { topLevel = location.level === 1; }
    return topLevel;
  }
/**
 * filters the tree using the filter function when the filter input changes
 * @param {?} e the change event
 * @return {?}
 */
onFilterChange(e) {
    if (this.treeviewElement && this.treeviewElement.tree) {
      this.filterBy = e;
      if (this.filterBy !== '') {
        this.treeviewElement.tree.treeModel.filterNodes(node => this.filterFunction(node));
      } else {
        this.treeviewElement.tree.treeModel.clearFilter();
      }
      // this.updateTree();
    }
  }
/**
 * @hidden 
 * converts a node to an array of its type when the box is checked
 * @param {?} e the change event
 * @param {?} node the node being changed
 * @return {?}
 */
onArrayCheck(e, node) {
    if (e.checked === true) {
      node.isArray = true;
    } else {
      node.isArray = false;
    }
  }
/**
 * @hidden 
 * changes the type or key of a node when its field is changed
 * @param {?} e the change event
 * @param {?} node the node being changed
 * @param {?} field the field being changed
 * @return {?}
 */
valueChange(e, node, field) {
    let /** @type {?} */ newValue = e.srcElement ? e.srcElement.value : e.value;
    this.editNode(newValue, node, field);
  }
/**
 * @hidden 
 * toggles the lockEdit property of a node and its children
 * @param {?} node the node to be locked or unlocked
 * @param {?=} lock locks if true, unlocks if false
 * @return {?}
 */
toggleLock(node, lock?) {
    node.lockEdit = lock ? lock : !node.lockEdit;
    if (node._children) {
      node._children.forEach(child => {
        this.toggleLock(child, node.lockEdit);
      })
    }
  }
/**
 * @hidden 
 * creates new blank node if enter is pressed on the last child in a tree
 * @param {?} e the event emitted
 * @param {?} tree the tree that contains the node
 * @param {?} node the node that the key was pressed on
 * @return {?}
 */
onKeyUp(e, tree, node) {
    if (e.key === 'Enter') {
      let /** @type {?} */ location = this.findNode(tree, node);
      let /** @type {?} */ parent = location.parent;
      if (parent.indexOf(location.child) === parent.length - 1) {
        this.addBlankNode(parent);
      } else {
        this.treeviewElement.tree.treeModel.focusNextNode();
      }
    }
  }
/**
 * @hidden 
 * takes the array of nodes at the root of the tree and converts back to JSON schema
 * @param {?} treeItems the items of the treeview
 * @return {?}
 */
convertToJSON(treeItems) {
    let /** @type {?} */ results = {};
    treeItems.forEach(node => {
      results[node.key] = this.convertJSONNode(node);
    });
    return results;
  }
/**
 * @hidden 
 * converts a given node back to JSON
 * @param {?} node the node input
 * @return {?}
 */
convertJSONNode(node) {
    let /** @type {?} */ result = null;
    if (typeof node.value !== 'object') {
      return node.value;
    } else if (Array.isArray(node.value)) {
      if (node.type !== 'object') {
        result = node.value;
      } else {
        result = []
        if (node._children) {
          node._children.forEach(childNode => {
            result.push(this.convertJSONNode(childNode));
          });
        }
      }
    } else if (node.value !== null) {
      result = {};
      if (node._children) {
        node._children.forEach(childNode => {
          result[childNode.key] = this.convertJSONNode(childNode);
        });
      }
    }
    return result;
  }
/**
 * converts a given node back to JSON
 * @return {?}
 */
convertData() {
    this.JsonOutput = this.convertToJSON(this.treeViewModel.items);
    setTimeout(() => {
      this.onDataConvert.emit(this.JsonOutput);
    }, 0);
  }
/**
 * @hidden 
 * changes illegalOperation to true briefly to flash red
 * @return {?}
 */
flashIllegal() {
    this.illegalOperation = true;
    setTimeout(() => {
      this.illegalOperation = false;
    }, 300);
  }
/**
 * @hidden 
 * Method to select which function to run
 * @param {?} name  function alias
 * @param {?} item  item from which action was triggered
 * @return {?}
 */
runAction(name, item) {
    switch (name) {
      case 'moveUp':        this.moveNode(this.treeViewModel.items, item, 'up'); break;
      case 'moveDown':      this.moveNode(this.treeViewModel.items, item, 'down'); break;
      case 'delete':        this.findAndRemoveNode(this.treeViewModel.items, item); break;
      case 'insert':        this.addBlankNode(item); break;
      case 'indent':        this.indentNode(this.treeViewModel.items, item); break;
      case 'outdent':       this.outdentNode(this.treeViewModel.items, item); break;
      case 'clone':         this.duplicateNode(this.treeViewModel.items, item); break;
      case 'expandAll':     this.expandAll(); break;
      case 'collapseAll':   this.collapseAll(); break;
      case 'addToTopLevel': this.addBlankNode(this.treeViewModel.items); break;
      case 'transform':     this.convertData(); break;
      default:              this.onCustomAction.emit({ name: name, item: item }); break;
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'cf-json-editor',
  template: "<div *ngIf=\"_inited && properties?.display\" class=\"json-editor {{activeTemplate.style?.container?.class}} {{styling?.container?.class}} {{getDynamicClasses(activeTemplate.style?.container?.dynamicClass)}} {{getDynamicClasses(styling?.container?.dynamicClass)}}\" [ngClass]=\"'cf-json-editor'\"> <div class=\"{{activeTemplate.style?.header?.class}} {{styling?.header?.class}} {{getDynamicClasses(activeTemplate.style?.header?.dynamicClass)}} {{getDynamicClasses(styling?.header?.dynamicClass)}}\" [ngClass]=\"'cf-json-editor-header'\"><ng-content select=\"cf-json-editor-header\"></ng-content></div> <cf-treeview *ngIf=\"treeViewModel\" (onMoveNode)=\"onMoveNode($event)\" [properties]=\"treeViewModel\" [styling]=\"styling?.treeview\" [ngClass]=\"{'illegal-operation': illegalOperation}\"> <ng-template #itemTpl let-item=\"item\"> <div [ngClass]=\"{'buttons-hover':properties.showButtons === 'hover'}\"> <div *ngIf=\"!item.lockEdit\"> <p *ngIf=\"properties.lockKeys\" class=\"keys\">{{item.key}}:</p>             <input *ngIf=\"!properties.lockKeys\" class=\"tree-field\" value=\"{{item.key}}\" (change)=\"valueChange($event, item, 'key')\"/> <input class=\"tree-field\" [value]=\"item.isArray ? 'array' : item.type === 'object' ? 'object' : item.value\" (change)=\"valueChange($event, item, 'value')\"/> <div class=\"button-section\" [ngClass]=\"{'row-button-hidden':properties.showButtons !== 'always'}\"><md-icon (click)=\"toggleLock(item)\">lock_outline</md-icon></div> <div class=\"button-section\" [ngClass]=\"{'row-button-hidden':properties.showButtons !== 'always'}\" *ngFor=\"let section of properties?.rowButtons\"> <md-icon class=\"{{button.icon.startsWith('fa') ? 'fa ' + button.icon : 'material-icons'}}\" *ngFor=\"let button of section\" (click)=\"runAction(button.name, item)\">{{button.icon.startsWith('fa') ? '' : button.icon}}</md-icon></div> </div> <div *ngIf=\"item.lockEdit\"> <p class=\"no-edit\" *ngIf=\"properties.lockKeys || (item.key !== 'array' && !isTopLevel(treeViewModel.items, item))\" (change)=\"schemaValueChange($event, item, 'key')\">{{item.key}}</p> <input *ngIf=\"item.key !== 'array' && isTopLevel(treeViewModel.items, item) && !properties.lockKeys\" class=\"tree-field\" placeholder=\"{{item.key}}\" (change)=\"schemaValueChange($event, item, 'key')\" (keyup)=\"onKeyUp($event, treeViewModel.items, item)\"/>                 <p class=\"no-edit no-edit-type\">{{item.isArray ? 'array ' + '(' + item.type + ')' : item.type}}</p> <div *ngIf=\"isTopLevel(treeViewModel.items, item)\" class=\"button-section\"><md-icon (click)=\"toggleLock(item)\">lock_outline</md-icon><md-icon (click)=\"findAndRemoveNode(treeViewModel.items, item)\">delete</md-icon></div> </div> </div> </ng-template> </cf-treeview> </div>",
  styles: [":host ::ng-deep [hidden] { display: none !important; } :host ::ng-deep .tree-field { display: inline-block; background-color: white; color: grey; height: 28px; outline: none; border: 1px lightgrey solid; padding-left: 16px; vertical-align: bottom; font-size: 16px; line-height: 25px; } :host ::ng-deep .tree-field::placeholder { color: grey; } :host ::ng-deep .keys, :host ::ng-deep .no-edit { vertical-align: bottom; color: grey; font-size: 16px; height: 32px; width: 170px; padding: 0px; margin: 0px; padding-left: 16px; display: inline-block; line-height: 32px; } :host ::ng-deep .keys { padding-left: 0px; } :host ::ng-deep .row-button-hidden { visibility: hidden !important; } :host ::ng-deep .no-edit { background-color: whitesmoke; } :host ::ng-deep .no-edit-type { margin-left: 8px; } :host ::ng-deep .button-section { margin-left: 12px; display: inline-flex; } :host ::ng-deep .button-section md-icon { display: flex; align-items: center; justify-content: center; width: 30px; height: 30px; line-height: 0px; border: 1px transparent solid; background-color: transparent; color: lightgrey; } :host ::ng-deep .button-section md-icon:hover { border: 1px lightgrey solid; } :host ::ng-deep .buttons-hover > div { display: flex; align-items: center; } :host ::ng-deep .buttons-hover > div > input:first-child { margin-right: 4px; } :host ::ng-deep .buttons-hover:hover md-icon { visibility: visible !important; } :host ::ng-deep tree-node-expander { margin-top: 6px; } :host ::ng-deep .toggle-children { border-left-width: 5px !important; border-left-color: grey !important; } :host ::ng-deep .tree-node-active .row-button { visibility: visible !important; } :host ::ng-deep .tree-node-active .node-content-wrapper { transition: background-color 200ms; } :host ::ng-deep .illegal-operation ::ng-deep .tree-node-active .node-content-wrapper { background-color: #fcdae1 !important; } "],
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: TemplateService, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'treeviewElement': [{ type: ViewChild, args: [CfTreeviewComponent, ] },],
'properties': [{ type: Input },],
'styling': [{ type: Input },],
'lockKeys': [{ type: Input, args: ['lockKeys', ] },],
'showButtons': [{ type: Input, args: ['showButtons', ] },],
'data': [{ type: Input, args: ['data', ] },],
'rowButtons': [{ type: Input, args: ['rowButtons', ] },],
'onCustomAction': [{ type: Output },],
'onDataConvert': [{ type: Output },],
};
}

function CfJsonEditorComponent_tsickle_Closure_declarations() {
/** @type {?} */
CfJsonEditorComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
CfJsonEditorComponent.ctorParameters;
/** @type {?} */
CfJsonEditorComponent.propDecorators;
/**
 * @hidden 
 * Link to the cf-treeview-component
 * @type {?}
 */
CfJsonEditorComponent.prototype.treeviewElement;
/**
 * <p>JsonEditorModel object.</p>
 * <p>Example of model:</p>
 * <pre>
 * {{'{'}}
 *  data: this.myJsonObject,
 *  lockKeys: true,
 *  showButtons: "hover",
 *  rowButtons: [
 *    [
 *      {{'{'}} "name": "moveUp", "icon": "keyboard_arrow_up" {{'}'}},
 *      {{'{'}} "name": "moveDown", "icon": "keyboard_arrow_down" {{'}'}}
 *    ],
 *    [
 *      {{'{'}} "name": "indent", "icon": "format_indent_increase" {{'}'}},
 *      {{'{'}} "name": "outdent", "icon": "format_indent_decrease" {{'}'}}
 *    ],
 *    [
 *      {{'{'}} "name": "clone", "icon": "content_copy" {{'}'}},
 *      {{'{'}} "name": "delete", "icon": "delete" {{'}'}},
 *      {{'{'}} "name": "insert", "icon": "playlist_add" {{'}'}}
 *    ]
 *  ]
 * {{'}'}}
 * </pre>
 * <h4>data: object</h4>
 * <p>JSON input to be converted into tree</p>
 * <h4>lockKeys: boolean</h4>
 * <p>Whether to prevent keys from being edited. Default: true.</p>
 * <h4>rowButtons: [ JsonEditorRowButtonModel[] ]</h4>
 * <p>Array of buttons to be rendered per level</p>
 * <h4>showButtons: string</h4>
 * <p>Circumstances to show buttons in. Can be: hover, select or always.</p>
 * @type {?}
 */
CfJsonEditorComponent.prototype.properties;
/**
 * Object for all styling properties:
 * <h4>container: StylingModel</h4>
 * <p>Styling properties for main json-editor container</p>
 * <h4>header: StylingModel</h4>
 * <p>Styling properties for header section</p>
 * <h4>treeview: TreeviewStylingModel</h4>
 * <p>Styling properties for cf-treeview</p>
 * @type {?}
 */
CfJsonEditorComponent.prototype.styling;
/**
 * Emit event with action (name with icon) and optional item from which action was triggered
 * @type {?}
 */
CfJsonEditorComponent.prototype.onCustomAction;
/**
 * Emit event with converted json object
 * @type {?}
 */
CfJsonEditorComponent.prototype.onDataConvert;
/**
 * @hidden 
 * <p>String being filtered on</p>
 * @type {?}
 */
CfJsonEditorComponent.prototype.filterBy;
/**
 * @hidden 
 * <p>toggled to true to flash a red warning sometimes</p>
 * @type {?}
 */
CfJsonEditorComponent.prototype.illegalOperation;
/**
 * @hidden 
 * <p>the array that the tree will be built around, since the Treeview takes an array, not JSON</p>
 * @type {?}
 */
CfJsonEditorComponent.prototype.tree;
/**
 * @hidden 
 * <p>Model for the underlying treeview component</p>
 * @type {?}
 */
CfJsonEditorComponent.prototype.treeViewModel;
/**
 * @hidden 
 * <p>property for output JSON to be written to</p>
 * @type {?}
 */
CfJsonEditorComponent.prototype.JsonOutput;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
